import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Utensils, Bell, ChefHat, Table2, ArrowRight, Clock, Repeat } from 'lucide-react';
import { useSessionStore } from '@renderer/store/useSessionStore';
import type { MesaVista, PedidoVista } from '@shared/types';
import { EntregarTurnoModal, mesasEntregables } from '@renderer/components/mesas/EntregarTurnoModal';
import { etiquetaEstadoPedido } from '@shared/estadoPedidoVista';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourDashboardMesero } from '@renderer/tours';

interface MesaConPedido extends MesaVista {
  pedido?: PedidoVista | null;
}

const REFRESCO_MS = 5_000;

export function DashboardMesero() {
  const navigate = useNavigate();
  const sesion = useSessionStore((s) => s.sesion);
  const [mesas, setMesas] = useState<MesaVista[]>([]);
  const [pedidos, setPedidos] = useState<PedidoVista[]>([]);
  // Sprint 172 — Entregar mis mesas a un compañero.
  const [entregando, setEntregando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = async () => {
    const [m, p] = await Promise.all([
      window.api.mesas.listar(),
      window.api.pedidos.listarPorEstado([
        'abierto',
        'pendiente_cocina',
        'cocina',
        'listo',
        'entregado',
      ]),
    ]);
    if (m.ok) setMesas(m.data);
    if (p.ok) setPedidos(p.data);
  };

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(t);
  }, []);

  if (!sesion) return null;

  /*
    Sprint 172 — Ya no se consulta la caja aquí. El mesero no tiene permiso de ver el corte (lleva el dinero
    del turno), así que esta pantalla se quedaba **siempre** en "La caja no está abierta". La regla sigue
    viva donde manda: el servidor no acepta pedidos con la caja cerrada y la toma del pedido lo dice.
  */
  // Sprint 172 — "Mío" es quien ATIENDE (la entrega de turno lo cambia), no quien capturó, y cuenta las
  // mesas juntadas. El servidor ya sólo le manda lo suyo; esto es la misma regla, para la pantalla.
  const pedidosMios = pedidos.filter((p) => (p.meseroAtiendeId ?? p.usuarioId) === sesion.usuarioId);
  const mesaPorPedido = new Map<number, PedidoVista>();
  for (const p of pedidosMios) {
    for (const id of p.mesaIds && p.mesaIds.length > 0 ? p.mesaIds : p.mesaId ? [p.mesaId] : []) mesaPorPedido.set(id, p);
  }
  const numeroDeMesa = (id: number | null | undefined) => mesas.find((m) => m.id === id)?.numero ?? id;
  const entregables = mesasEntregables(mesas, pedidosMios, sesion.usuarioId);

  const misMesas: MesaConPedido[] = mesas
    .filter((m) => mesaPorPedido.has(m.id))
    .map((m) => ({ ...m, pedido: mesaPorPedido.get(m.id) ?? null }));

  const atencionListo = pedidosMios.filter((p) => p.estado === 'listo');
  const atencionCuenta = pedidosMios.filter(
    (p) => p.cuentaSolicitada && p.estado !== 'pagado' && p.estado !== 'cancelado',
  );
  const necesitaAtencion = atencionListo.length + atencionCuenta.length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Hola, {sesion.nombre}</h1>
          <p className="text-slate-500" data-tour="dm-atencion">
            {necesitaAtencion > 0
              ? `${necesitaAtencion} mesa${necesitaAtencion === 1 ? '' : 's'} necesita${necesitaAtencion === 1 ? '' : 'n'} tu atención`
              : '¿Qué mesa toca atender?'}
          </p>
        </div>
        <HelpButton tour={tourDashboardMesero} />
      </div>

      <div className="grid gap-4 md:grid-cols-2" data-tour="dm-ctas">
        <button
          onClick={() => navigate('/pos?tipo=comer_aqui')}
          className="group flex items-center gap-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 p-5 text-left text-white shadow-lg transition hover:shadow-xl hover:scale-[1.01]"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20">
            <Utensils className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide opacity-80">Tomar</div>
            <div className="text-xl font-bold">Nuevo pedido</div>
            <div className="text-xs opacity-90">Comer aquí</div>
          </div>
          <ArrowRight className="h-5 w-5 opacity-70 group-hover:translate-x-1 transition" />
        </button>

        <button
          onClick={() => navigate('/mesas')}
          className="group flex items-center gap-4 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-5 text-left text-white shadow-lg transition hover:shadow-xl hover:scale-[1.01]"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20">
            <Table2 className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide opacity-80">Ver</div>
            <div className="text-xl font-bold">Mesas del salón</div>
            <div className="text-xs opacity-90">Estado y pedidos vivos</div>
          </div>
          <ArrowRight className="h-5 w-5 opacity-70 group-hover:translate-x-1 transition" />
        </button>
      </div>

      {(atencionListo.length > 0 || atencionCuenta.length > 0) && (
        <div className="card">
          <h2 className="mb-3 flex items-center gap-2 font-semibold text-amber-700">
            <Bell className="h-5 w-5" />
            Atención requerida
          </h2>
          <div className="space-y-2">
            {atencionListo.map((p) => (
              <button
                key={`listo-${p.id}`}
                onClick={() => navigate('/pedidos')}
                className="flex w-full items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-left hover:bg-emerald-100 transition"
              >
                <ChefHat className="h-5 w-5 text-emerald-600" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">
                    Cocina lista — Pedido #{String(p.numeroPedido).padStart(5, '0')}
                  </div>
                  <div className="text-xs text-slate-600">
                    {p.mesaId ? `Mesa ${numeroDeMesa(p.mesaId)}` : 'A domicilio'} · llevar al cliente
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-emerald-600" />
              </button>
            ))}
            {atencionCuenta.map((p) => (
              <button
                key={`cuenta-${p.id}`}
                onClick={() => navigate('/pedidos')}
                className="flex w-full items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-left hover:bg-amber-100 transition"
              >
                <Bell className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">
                    Pidió cuenta — Pedido #{String(p.numeroPedido).padStart(5, '0')}
                  </div>
                  <div className="text-xs text-slate-600">
                    {p.mesaId ? `Mesa ${numeroDeMesa(p.mesaId)}` : '—'} · cobrar
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-amber-600" />
              </button>
            ))}
          </div>
        </div>
      )}

      {aviso && (
        <div role="status" className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-base font-medium text-emerald-900">
          {aviso}
        </div>
      )}

      <div data-tour="dm-mesas">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            Mis mesas activas {misMesas.length > 0 && <span className="text-slate-600">({misMesas.length})</span>}
          </h2>
          {entregables.length > 0 && (
            <button
              type="button"
              onClick={() => { setAviso(null); setEntregando(true); }}
              className="flex min-h-[44px] items-center gap-2 rounded-lg border-2 border-slate-300 px-4 text-base font-semibold text-slate-800 hover:border-primary"
            >
              <Repeat className="h-5 w-5" /> Entregar mis mesas
            </button>
          )}
        </div>
        {misMesas.length === 0 ? (
          <div className="card text-center text-slate-400 py-8">
            <Table2 className="mx-auto mb-2 h-10 w-10 opacity-30" />
            <p className="text-sm">No tienes mesas abiertas.</p>
            <p className="text-xs">Toca "Nuevo pedido" para empezar.</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {misMesas.map((m) => (
              <button
                key={m.id}
                onClick={() => navigate('/mesas')}
                className="card flex flex-col items-start gap-2 p-4 hover:border-primary transition text-left"
              >
                <div className="flex w-full items-center justify-between">
                  <div className="text-2xl font-bold">Mesa {m.numero}</div>
                  <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-orange-700">
                    {humanizarEstadoMesa(m.estado)}
                  </span>
                </div>
                {m.pedido && (
                  <>
                    <div className="text-xs text-slate-500">
                      Pedido #{String(m.pedido.numeroPedido).padStart(5, '0')} ·{' '}
                      {/* Sprint 172 — En palabras de la cocina, no el nombre interno del estado. */}
                      {etiquetaEstadoPedido(m.pedido).texto}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Clock className="h-3 w-3" />
                      {tiempoTranscurrido(m.pedido.fechaCreacion)}
                    </div>
                  </>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <EntregarTurnoModal
        abierto={entregando}
        modo="entregar"
        mesas={entregables}
        onCerrar={() => setEntregando(false)}
        onListo={(m) => {
          setEntregando(false);
          setAviso(m);
          void cargar();
        }}
      />
    </div>
  );
}

function humanizarEstadoMesa(estado: string): string {
  switch (estado) {
    case 'disponible': return 'Disponible';
    case 'ocupada': return 'Ocupada';
    case 'fuera_de_servicio': return 'Fuera de servicio';
    default: return estado;
  }
}

function tiempoTranscurrido(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'recién';
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  return `${hr} h ${min % 60} min`;
}
